import { DataTable, Icon, Spinner, Text } from '@scale/scaleui-radix';
import { getTableColumns } from './JoinTableColumns';
import { useMemo } from 'react';
import {
  JoinTaskIdColumnInfo,
  JoinWorkerIdColumnInfo,
  JoinTaskIdColumns,
  JoinWorkerIdColumns,
} from '@scale/genai-compass-shared/cards/join_additional_data/columns';
import { ExportSource } from '@scale/genai-compass-shared/cards/join_additional_data/types';
import { AutoIdentifyColumn, hasColumnSubtype } from '../../../components/AutoIdentifyColumn';
import { Column, ColumnSubType, ColumnType } from '@scale/compass-shared/types';
import { useLatestSessionResultSchema } from '@scale/compass-ui/apiClient';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { ExecutionRunType } from '@scale/compass-shared/CompassExecutionLog';

interface TierColumnsStatusProps {
  isLoading: boolean;
}

const TierColumnsStatus = ({ isLoading }: TierColumnsStatusProps) => {
  return (
    <Text as="p" size="2" className="flex gap-2">
      {isLoading ? (
        <>
          <Spinner size="2" /> Loading TIER columns...
        </>
      ) : (
        <>
          <Icon label="Info" className="text-primary-600" icon={faCheckCircle} size="2" /> TIER
          columns loaded
        </>
      )}
    </Text>
  );
};

interface JoinTableProps {
  columnsToJoin: {
    column: JoinWorkerIdColumns | JoinTaskIdColumns;
    source: ExportSource | string;
  }[];
  onSelectionChange: (
    selectedKeys: {
      column: JoinWorkerIdColumns | JoinTaskIdColumns;
      source: ExportSource | string;
    }[],
  ) => void;
  columns: Column[];
}

interface WorkflowSchemaColumn {
  id: string;
  header: string;
}

interface WorkflowRow {
  key: string;
  value: string;
  subtypeId: string;
  header: string;
  source: string;
  type: string;
  description: string;
}

export const JoinTable = ({ columnsToJoin, onSelectionChange, columns }: JoinTableProps) => {
  // Currently set to only TIER
  const workflowId = 'cmp_84425a209fda1aa764c7f9931dd7108f';

  const hasWorkerIdColumn = hasColumnSubtype(columns, ColumnSubType.WORKER_ID);
  const hasTaskIdColumn = hasColumnSubtype(columns, ColumnSubType.TASK_ID);
  const columnHeaders = useMemo(() => getTableColumns(), []);

  // Fetch the latest session result schema using react-query
  const { data: workflowResult, isPending: loadingTIERColumns } = useLatestSessionResultSchema(
    workflowId,
    ExecutionRunType.SCHEDULED,
    { enabled: true },
  );

  // Memoize table data
  const tableData = useMemo(() => {
    const workflowSchemaColumns = workflowResult?.schema || [];
    let baseRows = [
      ...(hasWorkerIdColumn
        ? Object.entries(JoinWorkerIdColumnInfo).map(([key, value]) => ({
            ...value,
            key,
            value: value.header,
            subtypeId: 'workerId',
          }))
        : []),
      ...(hasTaskIdColumn
        ? Object.entries(JoinTaskIdColumnInfo).map(([key, value]) => ({
            ...value,
            key,
            value: value.header,
            subtypeId: 'taskId',
          }))
        : []),
    ];
    // Append workflow schema columns if hasWorkerIdColumn
    if (hasWorkerIdColumn && workflowSchemaColumns && workflowSchemaColumns.length > 0) {
      const workflowRows = workflowSchemaColumns.map((col: WorkflowSchemaColumn) => ({
        key: col.id,
        value: col.header,
        subtypeId: 'workerId',
        header: col.header,
        source: `${ExportSource.WORKFLOW}-workerId-${workflowId}`,
        type: ColumnType.TEXT,
        description: 'TIER: Growth Join Source',
      }));
      // Avoid duplicates by key
      const existingKeys = new Set(baseRows.map(row => row.key));
      baseRows = [
        ...baseRows,
        ...workflowRows.filter((row: WorkflowRow) => !existingKeys.has(row.key)),
      ];
    }
    return baseRows;
  }, [hasWorkerIdColumn, hasTaskIdColumn, workflowResult]);

  // Memoize row selection
  const currentRowSelection = useMemo(
    () =>
      tableData.reduce((acc, row, index) => {
        acc[index] = columnsToJoin.some(col => col.column === row.key && col.source === row.source);
        return acc;
      }, {} as Record<string, boolean>),
    [columnsToJoin, tableData],
  );

  const handleSelectionChange = (
    newSelection:
      | Record<string, boolean>
      | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => {
    const finalSelection =
      typeof newSelection === 'function' ? newSelection(currentRowSelection) : newSelection;

    const selectedKeys = Object.entries(finalSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([index]) => ({
        column: tableData[parseInt(index)].key as JoinWorkerIdColumns | JoinTaskIdColumns,
        source: tableData[parseInt(index)].source as ExportSource,
      }));

    onSelectionChange(selectedKeys);
  };

  return (
    <div>
      <DataTable.Root
        className="max-h-[300px] mt-2"
        columns={columnHeaders}
        data={tableData}
        enableRowSelection
        state={{ rowSelection: currentRowSelection }}
        enableColumnFilters={true}
        onRowSelectionChange={handleSelectionChange}
        initialState={{
          pagination: { pageIndex: 0, pageSize: 1000 },
          columnVisibility: { key: false },
        }}
      >
        {({ table }) => (
          <>
            <DataTable.Actions>
              <div className="flex flex-row justify-between w-full items-end">
                <div className="flex flex-col gap-1">
                  <AutoIdentifyColumn columns={columns} columnSubType={ColumnSubType.WORKER_ID} />
                  <AutoIdentifyColumn columns={columns} columnSubType={ColumnSubType.TASK_ID} />

                  <TierColumnsStatus isLoading={loadingTIERColumns} />
                </div>

                <DataTable.ActionsSearch />
              </div>
            </DataTable.Actions>
            <DataTable.Table>
              <DataTable.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <DataTable.HeaderGroup key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <DataTable.ColumnHeaderCell key={header.id} header={header}>
                        {header.column.getCanFilter() ? (
                          <DataTable.ColumnHeaderFilter header={header} />
                        ) : null}
                      </DataTable.ColumnHeaderCell>
                    ))}
                  </DataTable.HeaderGroup>
                ))}
              </DataTable.Header>
              <DataTable.Body>
                {table.getRowModel().rows.map(row => (
                  <DataTable.Row key={row.id} row={row}>
                    {row.getVisibleCells().map(cell => (
                      <DataTable.Cell key={cell.id} cell={cell} />
                    ))}
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable.Table>
          </>
        )}
      </DataTable.Root>
    </div>
  );
};
